import React, {useMemo, useRef, useState} from "react";
import {Spinner} from "react-bootstrap";
import {useAppSelector} from "../../hooks";
import {selectAllEmployees} from "../../../features/AppDataSlice";
import {useCollectionData} from "react-firebase-hooks/firestore";
import {collection, doc, setDoc, deleteDoc} from "firebase/firestore";
import {appFirestore} from "../../../index";
import {trucksFBConverter} from "../trucks/TrucksFBConverter";
import {useFuelInvoices} from "./useFuelInvoices";
import RRTable from "../../components/rr-table/RRTable";
import {RRFuelInvoiceType} from "../../types/RRFuelInvoiceType";
import EditDeleteConfirm from "../../components/EditDeleteConfirm";
import {dateToString, dateWithMaxTime, dateWithZeroTime, showErrorsListInToast} from "../../tools";
import {RRTruckType} from "../../types/RRTruckType";
import {EMPTY_RR_INVOICE} from "../../types/RRFuelInvoiceType";
import * as XLSX from "xlsx";
import { v4 as uuidv4 } from 'uuid';
import FuelInvoicesFilter, {Filters} from "./FuelInvoicesFilter";
import FuelInvoiceEditModal from "./FuelInvoiceEditModal";
import FuelInvoicesStats from "./FuelInvoicesStats";
import {AddIconButton, PrintIconButton, XlsIconButton} from "../../components/IconButtons";
import {sortFuelInvoicesByDateAndMileage} from "./fuelInvoiceSorting";

const FuelInvoicesView: React.FC = () => {
    const [filters, setFilters] = useState<Filters>({
        periodFrom: dateWithZeroTime(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        periodTo: dateWithMaxTime(new Date()),
        driverId: "",
        truckId: "",
    });
    const [editInvoice, setEditInvoice] = useState<RRFuelInvoiceType | null>(null);
    const employees = useAppSelector(selectAllEmployees);

    const trucksCollection = useMemo(
        () => collection(appFirestore, "trucks").withConverter(trucksFBConverter),
        []
    );
    const [trucks, loadingTrucks, errorTrucks] = useCollectionData<RRTruckType>(trucksCollection);

    // added: memoized sorted trucks list (sorted by name) used for SelectInputField options
    const sortedTrucks: RRTruckType[] = useMemo<RRTruckType[]>(() => {
        return (trucks || []).slice().sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
    }, [trucks]);

    const {invoices, loading, error} = useFuelInvoices({
        periodFrom: filters.periodFrom || undefined,
        periodTo: filters.periodTo || undefined,
        driverId: filters.driverId || undefined,
        truckId: filters.truckId || undefined,
    });
    const sortedInvoices = useMemo<RRFuelInvoiceType[]>(
        () => sortFuelInvoicesByDateAndMileage(invoices),
        [invoices]
    );

    const tableRef = useRef<HTMLTableElement>(null);
    const statsTableRef = useRef<HTMLTableElement>(null);
    const [confirmId, setConfirmId] = useState<string | null>(null);

    const handleAddInvoice = () => {
        setEditInvoice({...EMPTY_RR_INVOICE});
    };

    const handleEditInvoice = (invoice: RRFuelInvoiceType) => {
        setEditInvoice(invoice);
    };

    const closeModal = () => setEditInvoice(null);

    const handleSaveInvoice = async (invoice: RRFuelInvoiceType) => {
        try {
            const payload = {...invoice, id: invoice.id || uuidv4()};
            await setDoc(doc(appFirestore, "fuelInvoices", payload.id), payload);
            closeModal();
        } catch (e: any) {
            showErrorsListInToast("Грешка", [e.message]);
        }
    };

    const handleDeleteInvoice = async (invoice: RRFuelInvoiceType) => {
        if (!invoice.id) return;
        try {
            await deleteDoc(doc(appFirestore, "fuelInvoices", invoice.id));
        } catch (e: any) {
            showErrorsListInToast("Грешка", [e.message]);
        }
    };

    const exportExcel = () => {
        if (!tableRef.current) return;
        const workbook = XLSX.utils.table_to_book(tableRef.current, {raw: true});
        XLSX.writeFile(workbook, "fuel-invoices.xlsx");
    };

    const exportStats = () => {
        if (!statsTableRef.current) return;
        const workbook = XLSX.utils.table_to_book(statsTableRef.current, {raw: true});
        XLSX.writeFile(workbook, "fuel-invoices-stats.xlsx");
    };

    const printElement = (el: HTMLElement | null, title: string = '', filtersParam?: { periodFrom?: Date | undefined; periodTo?: Date | undefined; truckId?: string; driverId?: string }) => {
        if (!el) return;

        // Resolve filter display values
        const pf = filtersParam || {};
        const fromStr = dateToString(pf.periodFrom as Date | null | undefined);
        const toStr = dateToString(pf.periodTo as Date | null | undefined);
        const truck = (trucks || []).find(t => t.id === pf.truckId) as any;
        const truckStr = pf.truckId ? (truck ? `${truck.name}${truck.plate ? ' (' + truck.plate + ')' : ''}` : pf.truckId) : 'Всички';
        const driver = (employees || []).find((e: any) => e.id === pf.driverId);
        const driverStr = pf.driverId ? (driver ? driver.name : pf.driverId) : 'Всички';

        const headerHtml = `<div style="margin-bottom:12px;"><h2 style="margin:0 0 6px 0;">${title}</h2>` +
            `<div style="font-size:0.95rem;color:#333">От: ${fromStr || '-'}&nbsp;&nbsp;До: ${toStr || '-'}&nbsp;&nbsp;Превозно средство: ${truckStr}&nbsp;&nbsp;Шофьор: ${driverStr}</div></div>`;

        const html = `<!doctype html>
            <html lang="bg">
            <head>
              <meta charset="utf-8"/>
              <title>${title}</title>
              <style>
                body { font-family: Arial, Helvetica, sans-serif; margin: 20px; }
                table { border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid #ccc; padding: 6px 8px; }
                th { background: #f7f3ec; }
                .text-end { text-align: right; }
            </style>
            </head>
            <body>
              ${headerHtml}
              ${el.outerHTML}
            </body>
            </html>`;

        const w = window.open('', '_blank');
        if (!w) return;
        w.document.open();
        w.document.write(html);
        w.document.close();
        w.focus();
        // give it a short timeout to render before print
        setTimeout(() => { w.print(); w.close(); }, 200);
    };

    const printTable = () => printElement(tableRef.current, 'Зареждания с гориво', filters);

    const filteredEmployees = useMemo(() => {
        const fromDate = filters.periodFrom || new Date();
        return (employees || [])
            .filter(e => e.isLogistics && e.isEmployee && e.startDate && e.startDate.getTime() <= fromDate.getTime())
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [employees, filters.periodFrom]);

    // totals for table (price, fuel, delta km, avg price/km, total km, avg consumption/km)
    const totals = useMemo(() => {
        // totalPrice here represents total value (Цена * Количество) across invoices
        const totalPrice = (invoices || []).reduce((s, it) => s + (Number(it.price || 0) * Number(it.fuelAmount || 0)), 0);
        const totalFuel = (invoices || []).reduce((s, it) => s + Number(it.fuelAmount || 0), 0);
        const totalDelta = (invoices || []).reduce((s, it) => s + (Number(it.endKm || 0) - Number(it.startKm || 0)), 0);
        const totalKm = (invoices || []).reduce((s, it) => s + Number(it.totalKm || 0), 0);
        const avgPriceForKm = totalDelta > 0 ? totalPrice / totalDelta : 0;
        const avgConsumptionPerKm = totalDelta > 0 ? totalFuel / totalDelta : 0;
        return {totalPrice, totalFuel, totalDelta, avgPriceForKm, totalKm, avgConsumptionPerKm};
    }, [invoices]);

    // group by truck stats
    const statsByTruck = useMemo(() => {
        const map = new Map<string, {truckId: string; name: string; plate: string; totalPrice: number; totalFuel: number; totalDelta: number; totalKm: number}>();
        (invoices || []).forEach(inv => {
            const tid = inv.truckId || "";
            const truck = (trucks || []).find(t => t.id === tid);
            const name = truck?.name || "(Няма)";
            const plate = truck?.plate || "";
            const existing = map.get(tid) || {truckId: tid, name, plate, totalPrice: 0, totalFuel: 0, totalDelta: 0, totalKm: 0};
            // totalPrice per truck is the total value (price * fuelAmount)
            existing.totalPrice += Number(inv.price || 0) * Number(inv.fuelAmount || 0);
            existing.totalFuel += Number(inv.fuelAmount || 0);
            existing.totalDelta += (Number(inv.endKm || 0) - Number(inv.startKm || 0));
            existing.totalKm += Number(inv.totalKm || 0);
            map.set(tid, existing);
        });
        return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [invoices, trucks]);

    // group by driver stats
    const statsByDriver = useMemo(() => {
        const map = new Map<string, {driverId: string; name: string; totalPrice: number; totalFuel: number; totalDelta: number; totalKm: number}>();
        (invoices || []).forEach(inv => {
            const did = inv.driverId || "";
            const driver = (employees || []).find(e => e.id === did);
            const name = driver?.name || "(Няма)";
            const existing = map.get(did) || {driverId: did, name, totalPrice: 0, totalFuel: 0, totalDelta: 0, totalKm: 0};
            // price contribution is price * fuelAmount
            existing.totalPrice += Number(inv.price || 0) * Number(inv.fuelAmount || 0);
            existing.totalFuel += Number(inv.fuelAmount || 0);
            existing.totalDelta += (Number(inv.endKm || 0) - Number(inv.startKm || 0));
            existing.totalKm += Number(inv.totalKm || 0);
            map.set(did, existing);
        });
        return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [invoices, employees]);

    return (
        <div className="p-3">
            <h3>Зареждания с гориво</h3>
            <FuelInvoicesFilter
                filters={filters}
                setFilters={setFilters}
                sortedTrucks={sortedTrucks}
                filteredEmployees={filteredEmployees}
                onAdd={handleAddInvoice}
                onPrint={printTable}
                onExport={exportExcel}
            />

            {(loading || loadingTrucks) && <Spinner animation="border"/>}
            {error && <div className="text-danger">Грешка при зареждане на данните: {String((error as any)?.message ?? error)}</div>}
            {errorTrucks && <div className="text-danger">Грешка при зареждане на превозните средства</div>}

            <div className="row">
                <div className="col-12 col-lg-8">
                    <div className="mb-2">
                        <AddIconButton onClick={handleAddInvoice} />&nbsp;
                        <PrintIconButton onClick={printTable} />&nbsp;
                        <XlsIconButton onClick={exportExcel} />
                    </div>
                 <RRTable ref={tableRef}>
                        <thead>
                        <tr>
                            <th className="text-center"></th>
                            <th>Фактура №</th>
                            <th>Дата</th>
                            <th>Превозно средство</th>
                            <th>Рег.номер</th>
                            <th>Шофьор</th>
                            <th className="text-end">Цена за литър</th>
                            <th className="text-end">Количество гориво</th>
                            <th className="text-end">Стойност</th>
                            <th className="text-end">Начален км</th>
                            <th className="text-end">Краен км</th>
                            <th className="text-end">Изм.км</th>
                            <th className="text-end">Разх./км</th>
                            <th className="text-end">Цена/км</th>
                            <th className="text-end">Общо км</th>
                            <th>Бележки</th>
                        </tr>
                        </thead>
                        <tbody>
                        {sortedInvoices.map((invoice) => (
                            <tr key={invoice.id}>
                                <td className={"w-50px text-nowrap"}>
                                    <EditDeleteConfirm
                                        confirm={confirmId === invoice.id}
                                        onEdit={() => handleEditInvoice(invoice)}
                                        onDelete={() => setConfirmId(invoice.id || '')}
                                        onConfirm={() => { handleDeleteInvoice(invoice); setConfirmId(null); }}
                                        onReject={() => setConfirmId(null)}
                                    />
                                </td>
                                <td>{invoice.invNumber}</td>
                                <td>{dateToString(invoice.invDate)}</td>
                                <td>{trucks?.find(t => t.id === invoice.truckId)?.name || ""}</td>
                                <td>{trucks?.find(t => t.id === invoice.truckId)?.plate || ""}</td>
                                <td>{employees.find(e => e.id === invoice.driverId)?.name || ""}</td>
                                <td className="text-end">{invoice.price.toFixed(2)}</td>
                                <td className="text-end">{invoice.fuelAmount.toFixed(2)}</td>
                                <td className="text-end">{(Number(invoice.price || 0) * Number(invoice.fuelAmount || 0)).toFixed(2)}</td>
                                <td className="text-end">{invoice.startKm}</td>
                                <td className="text-end">{invoice.endKm}</td>
                                <td className="text-end">{(Number(invoice.endKm || 0) - Number(invoice.startKm || 0)).toString()}</td>
                                <td className="text-end">{(Number(invoice.endKm || 0) - Number(invoice.startKm || 0)) > 0 ? (Number(invoice.fuelAmount || 0) / (Number(invoice.endKm || 0) - Number(invoice.startKm || 0)) * 100).toFixed(2) : '0.00'}</td>
                                <td className="text-end">{invoice.avgPriceForKm.toFixed(2)}</td>
                                <td className="text-end">{invoice.totalKm}</td>
                                <td>{invoice.notes}</td>
                            </tr>
                        ))}
                        </tbody>
                    </RRTable>
                </div>
                <div className="col-12 col-lg-4">
                    <FuelInvoicesStats
                        statsByTruck={statsByTruck}
                        statsByDriver={statsByDriver}
                        totals={totals}
                        statsTableRef={statsTableRef}
                        onExportStats={exportStats}
                        onPrint={() => printElement(statsTableRef.current, 'Статистика', filters)}
                    />
                </div>
             </div>

            <FuelInvoiceEditModal
                invoice={editInvoice}
                trucks={trucks}
                filteredEmployees={filteredEmployees}
                onSave={handleSaveInvoice}
                onClose={closeModal}
            />
        </div>
    );
};

export default FuelInvoicesView;
